import { t } from "@travily/i18n";
import type TripsRow from "@travily/types/models/TripsRow";
import type { TripsRowCanTrait } from "@travily/types/models/TripsRow";
import { ActionMenu } from "@ui/components/ActionMenu";
import { AvatarStack } from "@ui/components/AvatarStack";
import { ExpandableText } from "@ui/components/ExpandableText";
import { Icon } from "@ui/components/Icon";
import { Image } from "@ui/components/Image";
import { OutlineButton } from "@ui/components/OutlineButton";
import { formatDateRange, getCountryName, getTemporalStatus } from "@ui/lib/format-date";
import { tv } from "@ui/lib/tv";
import { AccountAvatar } from "@/components/AccountAvatar/AccountAvatar";
import { PersonAvatar } from "@/components/PersonAvatar/PersonAvatar";
import type { TripOwner } from "./types";

const tripCard = tv({
  slots: {
    root: "flex flex-col gap-3 lg:gap-0 md:overflow-hidden md:rounded-3xl md:bg-white",
    contentWrapper: "contents md:flex md:flex-col md:gap-3 md:p-5 lg:gap-5 lg:p-7",
    header: "flex items-center justify-between px-5 md:px-0",
    headerLeft: "flex items-center gap-3 lg:gap-4",
    ownerName: "text-base font-bold leading-6 text-neutral-900 lg:text-lg",
    dateRange: "text-sm leading-5 text-neutral-500 lg:text-base lg:leading-6",
    description: "px-5 text-base leading-6 text-neutral-900 md:px-0 lg:text-lg",
    imageContainer: "relative aspect-square w-full overflow-hidden",
    imagePlaceholder: "size-full bg-neutral-100",
    image: "size-full object-cover",
    gradient: "pointer-events-none absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-b from-white/0 to-blue-900/70",
    overlay:
      "absolute inset-x-0 bottom-0 flex flex-col-reverse items-start gap-2 p-5 md:flex-row md:items-end md:justify-between md:gap-0 md:p-6",
    location: "flex flex-col text-white",
    locationName: "text-display-xs",
    locationCountry: "text-lg leading-6",
    dashedBorder: "pointer-events-none absolute inset-0 hidden size-full",
  },
  variants: {
    temporalStatus: {
      past: {
        root: "md:shadow-[0px_1px_1px_0px_rgba(0,0,0,0.05),0px_3px_3px_0px_rgba(0,0,0,0.05),0px_0px_0px_1px_rgba(0,0,0,0.1)]",
        imageContainer: "md:aspect-[820/488]",
        gradient: "md:h-[280px]",
      },
      current: {
        root: "md:shadow-[0px_1px_1px_0px_rgba(0,0,0,0.05),0px_3px_3px_0px_rgba(0,0,0,0.05),0px_0px_0px_3px_var(--color-red-300)]",
        imageContainer: "md:aspect-[820/488]",
        gradient: "md:h-[280px]",
      },
      future: {
        root: "md:relative",
        imageContainer: "md:aspect-[820/392]",
        dashedBorder: "md:block",
        gradient: "md:h-[224px]",
      },
    },
  },
  defaultVariants: {
    temporalStatus: "past",
  },
});

export interface TripCardProps {
  owner: TripOwner;
  onDelete?: () => void;
  onEdit?: () => void;
  trip: TripsRow & TripsRowCanTrait;
}

export function TripCard({ owner, onDelete, onEdit, trip }: TripCardProps) {
  const temporalStatus = getTemporalStatus(trip.start_date, trip.end_date);
  const styles = tripCard({ temporalStatus });

  return (
    <div className={styles.root()}>
      <div className={styles.contentWrapper()}>
        <div className={styles.header()}>
          <div className={styles.headerLeft()}>
            <AccountAvatar
              account={{ profile_type: owner.profile_type, avatar: owner.avatar, initial: owner.initial }}
              shape="square"
              size="44"
            />
            <div className="flex flex-col">
              <p className={styles.ownerName()}>{owner.display_name}</p>
              <p className={styles.dateRange()}>{formatDateRange(trip.start_date, trip.end_date)}</p>
            </div>
          </div>
          {(trip.can.edit || trip.can.destroy) && (
            <ActionMenu
              items={[
                trip.can.edit && {
                  id: "edit",
                  label: t("components.views.shared.trips.edit_trip"),
                  icon: <Icon name="edit" />,
                  onAction: onEdit,
                },
                trip.can.destroy && {
                  id: "delete",
                  label: t("components.views.shared.trips.delete_trip"),
                  icon: <Icon name="trash" />,
                  onAction: onDelete,
                  destructive: true,
                },
              ]}
              placement="bottom end"
              trigger={
                <OutlineButton aria-label={t("common.actions.more")} shape="circle" size="36">
                  <Icon name="more" />
                </OutlineButton>
              }
            />
          )}
        </div>

        {trip.description && (
          <ExpandableText className={styles.description()} lines={3}>
            {trip.description}
          </ExpandableText>
        )}
      </div>

      <div className={styles.imageContainer()}>
        <Image
          image={trip.photo ?? trip.map_image}
          sizes="(min-width: 768px) 820px, 100vw"
          className={styles.image()}
          fallback={<div className={styles.imagePlaceholder()} />}
        />
        <div className={styles.gradient()} />
        <div className={styles.overlay()}>
          <div className={styles.location()}>
            <p className={styles.locationName()}>{trip.location_name}</p>
            <p className={styles.locationCountry()}>{getCountryName(trip.location_country_code)}</p>
          </div>
          {trip.members.length > 0 && (
            <AvatarStack>
              {trip.members.map((member) => (
                <PersonAvatar
                  key={member.id}
                  className="border-4 border-white shadow-[0px_1.364px_0px_0px_rgba(0,0,0,0.08),0px_0px_0px_1.364px_rgba(0,0,0,0.05)]"
                  color={member.color}
                  person={{ avatar: member.avatar, initial: member.initial }}
                  shape="circle"
                  size="52"
                />
              ))}
            </AvatarStack>
          )}
        </div>
      </div>
      <svg aria-hidden="true" className={styles.dashedBorder()} fill="none">
        <rect
          x="1"
          y="1"
          rx="19"
          ry="19"
          stroke="var(--color-neutral-100)"
          strokeWidth="2"
          strokeDasharray="8 5"
          fill="none"
          style={{ width: "calc(100% - 2px)", height: "calc(100% - 2px)" }}
        />
      </svg>
    </div>
  );
}
